import type { ApiResponse } from '@ihs-widget/api/interfaces';

/** Generic: response has error or empty/invalid data */
const isApiResponseBroken = <T>(r: ApiResponse<T> | undefined): boolean => {
    if (!r) return true;
    if (r.error !== null) return true;
    if (r.data === null || r.data === undefined) return true;
    return false;
};

/** Overview: /api/overview - needs match_clock or match_events */
export const isValidOverviewResponse = (r: ApiResponse<unknown> | undefined): boolean => {
    if (isApiResponseBroken(r)) return false;
    const d = r!.data as Record<string, unknown> | null;
    if (typeof d !== 'object' || Array.isArray(d) || !d) return false;
    if (Object.keys(d).length === 0) return false;
    return !!(d.match_clock ?? d.match_events);
};

/** StatsConsole: /api/stats-console - needs non-empty statistics array */
export const isValidStatsConsoleResponse = (r: ApiResponse<unknown> | undefined): boolean => {
    if (isApiResponseBroken(r)) return false;
    const d = r!.data;
    return Array.isArray(d) && d.length > 0;
};

/** StatsTab: /api/stats-tab - needs match_statistics */
export const isValidStatsTabResponse = (r: ApiResponse<unknown> | undefined): boolean => {
    if (isApiResponseBroken(r)) return false;
    const d = r!.data as Record<string, unknown> | null;
    return !!(d && typeof d === 'object' && d.match_statistics != null);
};

/** PlayersTab: /api/players-tab - needs match_statistics */
export const isValidPlayersTabResponse = (r: ApiResponse<unknown> | undefined): boolean => {
    if (isApiResponseBroken(r)) return false;
    const d = r!.data as Record<string, unknown> | null;
    return !!(d && typeof d === 'object' && d.match_statistics != null);
};

/** H2H: /api/h2h - needs non-empty object */
export const isValidH2HResponse = (r: ApiResponse<unknown> | undefined): boolean => {
    if (isApiResponseBroken(r)) return false;
    const d = r!.data;
    return typeof d === 'object' && !Array.isArray(d) && d !== null && Object.keys(d).length > 0;
};

/** Lineup: /api/lineup - needs data (homeFormation/homeLines etc) */
export const isValidLineupResponse = (r: ApiResponse<unknown> | undefined): boolean => {
    if (isApiResponseBroken(r)) return false;
    const d = r!.data;
    return typeof d === 'object' && !Array.isArray(d) && d !== null && Object.keys(d).length > 0;
};

/** TeamNames: /api/team-names - needs home_team or away_team */
export const isValidTeamNamesResponse = (r: ApiResponse<unknown> | undefined): boolean => {
    if (isApiResponseBroken(r)) return false;
    const d = r!.data as Record<string, unknown> | null;
    if (typeof d !== 'object' || d === null || Array.isArray(d)) return false;
    return d.home_team != null || d.away_team != null;
};
